import React, {
  FormEvent,
  useCallback,
  useEffect,
  useMemo,
  useState,
} from 'react'
import configment from '@adc/configment'
import { loadStripe } from '@stripe/stripe-js/pure'
import { Button, Loader, Text } from '@adc/ui-core'
import { noop } from 'lodash'
import {
  Elements,
  PaymentElement,
  useElements,
  useStripe,
} from '@stripe/react-stripe-js'
import classnames from 'classnames/bind'
import styles from './styles.scss'
import { StripeError, StripePaymentElementChangeEvent } from '@stripe/stripe-js'

const cx = classnames.bind(styles)
const ARBITRARY_UX_ERROR_PREVENTION_THRESHOLD = 3
const ADC_STRIPE_TERMS_LINK = 'https://stripe.com/legal/consumer'
const ADC_STRIPE_PRIVACY_LINK = 'https://stripe.com/privacy'

type StripePaymentHookInput = {
  clientSecret: string
  isLoading: boolean
  isSetup: boolean
  message: string
  confirmPayment?: () => void
  paymentErrorHandler?: (e?: StripeError) => void
  customSubmitButton?: boolean
  paymentDisabled?: boolean
  customCTALabel?: string
  additionalInfo?: React.ReactNode
}
type FormValues = {
  isProcessing: boolean
  isDisabled?: boolean
  isSubmitDisabled?: boolean
  handleSubmit: (e?: FormEvent) => Promise<void>
}

type StripePaymentHookOutput = {
  StripePaymentSetupForm: JSX.Element
} & FormValues

type CheckoutFormHookInput = {
  confirmPayment?: () => void
  paymentErrorHandler?: (e?: StripeError) => void
  setFormValues: (formValues: FormValues) => void
  customSubmitButton?: boolean
  paymentDisabled?: boolean
  isSetup?: boolean
  customCTALabel?: string
  additionalInfo?: React.ReactNode
}
export const CheckoutForm = ({
  confirmPayment,
  paymentErrorHandler,
  customSubmitButton = false,
  setFormValues,
  paymentDisabled,
  customCTALabel,
  additionalInfo,
  isSetup = false,
}: CheckoutFormHookInput) => {
  const stripe = useStripe()
  const elements = useElements()
  // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment,@typescript-eslint/no-unsafe-call
  // const notifications = useApplicationNotifications()
  const [isProcessing, setIsProcessing] = useState(false)
  const [isFormComplete, setIsFormComplete] = useState(false)
  const [errorCount, setErrorCount] = useState<number>(0)

  const handleSubmit = useCallback(
    async (e?: FormEvent) => {
      e?.preventDefault()
      if (!stripe || !elements) {
        // Stripe.js has not yet loaded.
        // Make sure to disable form submission until Stripe.js has loaded.
        return
      }

      setIsProcessing(true)

      const { error } = await stripe[
        isSetup ? 'confirmSetup' : 'confirmPayment'
      ]({
        elements,
        confirmParams: {
          return_url: `${window.location.origin}/`,
        },
        redirect: 'if_required',
      })

      setIsProcessing(false)

      if (error) {
        const currentErrorCount = errorCount + 1
        setErrorCount(currentErrorCount)

        if (currentErrorCount >= ARBITRARY_UX_ERROR_PREVENTION_THRESHOLD) {
          // @ts-ignore this will be handled differently once we decide which errors are retryable and which don't
          error.type = 'non_retryable_error'
        }
        paymentErrorHandler && paymentErrorHandler(error)
        // eslint-disable-next-line @typescript-eslint/no-unsafe-call,@typescript-eslint/no-unsafe-member-access
        // Leaving this commented in case after release we realize we want toasts
        // notifications?.toast.open({
        //   // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access,@typescript-eslint/no-unsafe-assignment
        //   role: NOTIFICATION_ROLES.ERROR,
        //   title: 'Payment attempt was unsuccessful.',
        //   message:
        //     'Please try again or contact our support team for further assistance.',
        // })
      } else {
        // eslint-disable-next-line @typescript-eslint/no-unsafe-call,@typescript-eslint/no-unsafe-member-access
        // Leaving this commented in case after release we realize we want toasts
        // notifications?.toast.open({
        //   // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access,@typescript-eslint/no-unsafe-assignment
        //   role: NOTIFICATION_ROLES.SUCCESS,
        //   title: 'Your payment was processed successfully',
        // })
        confirmPayment && confirmPayment()
      }
    },
    [confirmPayment, elements, paymentErrorHandler, stripe, errorCount, isSetup]
  )

  const isDisabled = useMemo(
    () => isProcessing || !stripe || !elements || paymentDisabled,
    [elements, isProcessing, paymentDisabled, stripe]
  )

  const isSubmitDisabled = useMemo(
    () => isDisabled || !isFormComplete,
    [isDisabled, isFormComplete]
  )
  useEffect(() => {
    // eslint-disable-next-line @typescript-eslint/no-unsafe-call
    setFormValues({ isDisabled, isSubmitDisabled, isProcessing, handleSubmit })
  }, [isDisabled, handleSubmit, isProcessing, setFormValues, isSubmitDisabled])

  const options = useMemo(() => ({ readOnly: isDisabled }), [isDisabled])
  useEffect(() => {
    const paymentElement = elements && elements.getElement('payment')
    paymentElement?.update(options)
  }, [isDisabled, elements, options])

  const onPaymentElementChange = (e: StripePaymentElementChangeEvent) => {
    setIsFormComplete(e.complete)
  }

  // Doing this to apply any updates that may have been made during loading time and lost.
  const onPaymentElementReady = () => {
    const paymentElement = elements && elements.getElement('payment')
    paymentElement?.update(options)
  }

  return (
    <>
      {/* eslint-disable-next-line @typescript-eslint/no-misused-promises */}
      <form
        id='payment-form'
        onSubmit={handleSubmit}
        className={cx({ 'payment-disabled': isDisabled })}
      >
        <PaymentElement
          id='payment-element'
          options={options}
          onChange={onPaymentElementChange}
          onReady={onPaymentElementReady}
        />
        <Text.Body className={cx('terms')}>
          Payment provider{' '}
          <a href={ADC_STRIPE_TERMS_LINK} target='_blank' rel='noreferrer'>
            Terms
          </a>{' '}
          |{' '}
          <a href={ADC_STRIPE_PRIVACY_LINK} target='_blank' rel='noreferrer'>
            Privacy
          </a>
        </Text.Body>
        {!customSubmitButton && (
          <div className={cx('submit-button-container')}>
            <Button
              className={cx('payment-submit-button')}
              disabled={isSubmitDisabled}
              id='submit'
            >
              {isProcessing ? 'Processing ... ' : customCTALabel || 'Pay Now'}
            </Button>
            {Boolean(additionalInfo) && additionalInfo}
          </div>
        )}
      </form>
    </>
  )
}

const useStripePayment = ({
  clientSecret,
  isLoading,
  message,
  confirmPayment,
  paymentErrorHandler,
  customCTALabel,
  additionalInfo,
  customSubmitButton = false,
  paymentDisabled = false,
  isSetup,
}: StripePaymentHookInput): StripePaymentHookOutput => {
  const stripePublishableKey = configment.get('STRIPE_PUBLISHABLE_KEY')
  const stripePromise = useMemo(
    () => loadStripe(stripePublishableKey),
    [stripePublishableKey]
  )
  const [formValues, setFormValuesState] = useState<FormValues>({
    isProcessing: false,
    isDisabled: false,
    isSubmitDisabled: false,
    // eslint-disable-next-line @typescript-eslint/no-empty-function
    // @ts-ignore initial value is noop
    handleSubmit: noop,
  })
  // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
  const StripePaymentSetupForm = useMemo(() => {
    return (
      <>
        {isLoading ? (
          <Loader elmId='stripe_spinner' active={isLoading} inverted />
        ) : (
          <>
            {clientSecret && stripePromise && (
              <Elements
                stripe={stripePromise}
                options={{ clientSecret }}
                key={clientSecret}
              >
                <CheckoutForm
                  setFormValues={setFormValuesState}
                  confirmPayment={confirmPayment}
                  customSubmitButton={customSubmitButton}
                  paymentErrorHandler={paymentErrorHandler}
                  paymentDisabled={paymentDisabled}
                  customCTALabel={customCTALabel}
                  additionalInfo={additionalInfo}
                  isSetup={isSetup}
                />
              </Elements>
            )}
            {message && <div>{message}</div>}
          </>
        )}
      </>
    )
  }, [
    additionalInfo,
    clientSecret,
    confirmPayment,
    customCTALabel,
    customSubmitButton,
    isLoading,
    isSetup,
    message,
    paymentDisabled,
    paymentErrorHandler,
    stripePromise,
  ])

  // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
  return useMemo(
    () => ({ StripePaymentSetupForm, ...formValues }),
    [StripePaymentSetupForm, formValues]
  )
}

export default useStripePayment
